/**
 * ReportingCurrencyPrompt — ask, rather than assume, which currency to report in.
 *
 * Phase 85.5. `lib/defaultCurrency.js` has said the right thing since 2026-08-25:
 * a suggestion may preselect, it may never silently decide, because "a silent
 * default is how an account full of Indian statements ended up stored as USD:
 * nobody chose it". Display code ignored that and did
 * `profile?.base_currency || 'INR'` in a dozen pages. So a beta user whose
 * profile carried no currency at all was shown ₹ figures on 2026-08-26 — the
 * rupee was never his choice, it was our fallback wearing his name.
 *
 * This asks him. It appears only when `profiles.base_currency` is null, it
 * preselects a suggestion and says where the suggestion came from, and it will
 * not write anything until he presses the button.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Globe } from 'lucide-react';
import { setBaseCurrency } from '@/lib/currency';
import { suggestCurrency } from '@/lib/defaultCurrency';

// The currencies the FX layer carries rates for (lib/currency_context._ACTIVE).
export const REPORTING_CURRENCIES = [
  ['INR', 'Indian Rupee'], ['AED', 'UAE Dirham'], ['USD', 'US Dollar'],
  ['GBP', 'Pound Sterling'], ['EUR', 'Euro'], ['SGD', 'Singapore Dollar'],
  ['SAR', 'Saudi Riyal'], ['QAR', 'Qatari Riyal'], ['KWD', 'Kuwaiti Dinar'],
  ['OMR', 'Omani Rial'], ['BHD', 'Bahraini Dinar'], ['AUD', 'Australian Dollar'],
  ['CAD', 'Canadian Dollar'],
];

export default function ReportingCurrencyPrompt({ profile, token, onSaved }) {
  const suggestion = useMemo(() => suggestCurrency(profile), [profile]);
  const [choice, setChoice] = useState(suggestion.code || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The pages that render this mount before `profile` has arrived, so the
  // first render suggests nothing and the select stayed empty with the button
  // disabled while the copy claimed a preselection. Adopt the suggestion when
  // it turns up — but never overwrite a choice already made by hand.
  useEffect(() => {
    if (suggestion.code) setChoice((prev) => prev || suggestion.code);
  }, [suggestion.code]);

  // Already answered. Nothing to ask.
  if (profile?.base_currency) return null;

  const save = async () => {
    if (!choice) return;
    setSaving(true);
    setError(null);
    const res = await setBaseCurrency(token, choice);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'That did not save. Try again in a moment.');
      return;
    }
    onSaved?.(choice);
  };

  return (
    <div
      className="bg-card rounded-xl border border-border p-5 shadow-sm"
      style={{ borderLeft: '3px solid #163A32' }}
      data-testid="reporting-currency-prompt"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0 w-7 h-7 rounded-md flex items-center justify-center bg-evergreen/10">
          <Globe className="h-3.5 w-3.5 text-evergreen" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-charcoal mb-1">
            Which currency should we report in?
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed mb-4">
            You haven&apos;t chosen one yet.{' '}
            {profile?.currency
              ? `Until you do we have been showing totals in
                 ${String(profile.currency).toUpperCase()} — a default on your
                 profile, not your decision.`
              : `Until you do we are holding back combined totals rather than
                 picking a currency for you.`}{' '}
            Your accounts keep their own currencies either way; this is only the
            currency your totals are shown in, and you can change it later in
            Settings.
            {suggestion.code ? ` We have preselected ${suggestion.code} based on ${suggestion.from}.` : ''}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              className="px-3 py-2 rounded-lg border border-border bg-background text-sm text-charcoal focus:outline-none focus:ring-1 focus:ring-evergreen/40"
              data-testid="reporting-currency-select"
            >
              <option value="">Choose a currency…</option>
              {REPORTING_CURRENCIES.map(([code, name]) => (
                <option key={code} value={code}>{code} — {name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={save}
              disabled={!choice || saving}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-evergreen text-white transition-opacity hover:opacity-90 disabled:opacity-40"
              data-testid="reporting-currency-save"
            >
              {saving ? 'Saving…' : 'Use this currency'}
            </button>
          </div>
          {error && (
            <p className="text-xs text-[#A84E38] mt-2" data-testid="reporting-currency-error">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}
